import type { ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AXIS_TICK, CHART_MARGIN, legendText } from "@/components/chartChrome";
import { TooltipRow, TooltipShell } from "@/components/ChartTooltip";
import { ChartEmptyState } from "@/components/states";
import {
  formatChartLabel,
  formatMonthDay,
  formatNumber,
  formatPercent,
} from "@/lib/format";
import {
  formatOptInRate,
  formatWeekRange,
  optInRate,
  zoomedDomain,
  type CumulativeWeek,
} from "@/lib/shopperGrowth";

/**
 * Charts for the shopper growth dashboard. All three share one weekly
 * x-axis (Monday-start weeks) and one series mapping: orange (series 2) is
 * always the shoppers currently opted in to marketing; purple (series 1) is
 * the rest of the registered population — the remainder segment in the
 * bars, the registered line in the running totals; blue (series 3) is the
 * derived opt-in rate.
 */

// Called as a function, not rendered as a component: Recharts only
// recognizes an axis when it is a direct child of the chart.
function WeekAxis() {
  return (
    <XAxis
      dataKey="weekStart"
      tickFormatter={formatMonthDay}
      tick={AXIS_TICK}
      axisLine={{ stroke: "var(--viz-baseline)" }}
      tickLine={false}
      minTickGap={32}
    />
  );
}

/**
 * ChartTooltip's chrome, but rows come from the hovered week rather than the
 * payload, so a stacked chart can show its total and a rate.
 */
function WeekTooltip({
  active,
  payload,
  rows,
}: {
  active?: boolean;
  payload?: ReadonlyArray<{ payload?: unknown }>;
  rows: (week: CumulativeWeek) => ReactNode;
}) {
  const week = payload?.[0]?.payload as CumulativeWeek | undefined;
  if (!active || !week) return null;
  return (
    <TooltipShell
      title={`${formatWeekRange(week.weekStart)}${week.partial ? " (to date)" : ""}`}
    >
      {rows(week)}
    </TooltipShell>
  );
}

/** The current week is still filling in, so its bar is drawn lighter. */
function partialCells(data: CumulativeWeek[]) {
  return data.map((w) => (
    <Cell key={w.weekStart} fillOpacity={w.partial ? 0.45 : 1} />
  ));
}

/**
 * Weekly signups as stacked bars: bar height is the shoppers who registered
 * that week, the orange top is those currently opted in. Series keep slot
 * order (purple then orange) so the legend reads like the other charts. The
 * 1px surface stroke is the gap between the two segments.
 */
export function WeeklySignupsChart({
  weeks,
  height = 260,
}: {
  weeks: CumulativeWeek[];
  height?: number;
}) {
  if (weeks.length === 0) return <ChartEmptyState />;
  const data = weeks.map((w) => ({ ...w, notOptedIn: w.registered - w.optedIn }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={CHART_MARGIN} barCategoryGap="30%">
        <CartesianGrid stroke="var(--viz-grid)" vertical={false} />
        {WeekAxis()}
        <YAxis
          tickFormatter={(v: number) => formatChartLabel(v)}
          tick={AXIS_TICK}
          axisLine={false}
          tickLine={false}
          width={52}
          allowDecimals={false}
        />
        <Tooltip
          cursor={{ fill: "var(--viz-grid)", opacity: 0.4 }}
          content={
            <WeekTooltip
              rows={(w) => (
                <>
                  <TooltipRow label="Registered" value={formatNumber(w.registered)} />
                  <TooltipRow
                    color="var(--viz-series-2)"
                    kind="bar"
                    label="Opted in to marketing"
                    value={formatNumber(w.optedIn)}
                  />
                  <TooltipRow
                    color="var(--viz-series-1)"
                    kind="bar"
                    label="Not opted in"
                    value={formatNumber(w.registered - w.optedIn)}
                  />
                  <TooltipRow label="Opt-in rate" value={formatOptInRate(w.optedIn, w.registered)} />
                </>
              )}
            />
          }
        />
        <Legend
          verticalAlign="top"
          height={28}
          iconType="square"
          iconSize={10}
          formatter={legendText}
        />
        <Bar
          name="Not opted in"
          dataKey="notOptedIn"
          stackId="week"
          fill="var(--viz-series-1)"
          stroke="var(--viz-surface)"
          strokeWidth={1}
          maxBarSize={24}
          isAnimationActive={false}
        >
          {partialCells(data)}
        </Bar>
        <Bar
          name="Opted in to marketing"
          dataKey="optedIn"
          stackId="week"
          fill="var(--viz-series-2)"
          stroke="var(--viz-surface)"
          strokeWidth={1}
          maxBarSize={24}
          radius={[4, 4, 0, 0]}
          isAnimationActive={false}
        >
          {partialCells(data)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * All-time running totals over the window. The y-axis is zoomed to the
 * period (never below zero) so the slope reads; the bottom tick shows where
 * the axis starts.
 */
export function CumulativeShoppersChart({
  weeks,
  height = 280,
}: {
  weeks: CumulativeWeek[];
  height?: number;
}) {
  if (weeks.length === 0) return <ChartEmptyState />;
  const domain = zoomedDomain(
    Math.min(...weeks.map((w) => w.cumOptedIn)),
    Math.max(...weeks.map((w) => w.cumRegistered)),
  );
  // Compact labels (17.3K) unless the zoomed step is finer than their three
  // significant digits can show, e.g. 99.8K / 100K / 100.2K all reading
  // "100K"; then the ticks get exact numbers.
  const compact = domain.ticks.map(formatChartLabel);
  const tickLabel =
    new Set(compact).size === compact.length ? formatChartLabel : formatNumber;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={weeks} margin={CHART_MARGIN}>
        <CartesianGrid stroke="var(--viz-grid)" vertical={false} />
        {WeekAxis()}
        <YAxis
          domain={[domain.min, domain.max]}
          ticks={domain.ticks}
          tickFormatter={tickLabel}
          tick={AXIS_TICK}
          axisLine={false}
          tickLine={false}
          width={52}
        />
        <Tooltip
          cursor={{ stroke: "var(--viz-baseline)", strokeWidth: 1 }}
          content={
            <WeekTooltip
              rows={(w) => (
                <>
                  <TooltipRow
                    color="var(--viz-series-1)"
                    label="Registered"
                    value={formatNumber(w.cumRegistered)}
                  />
                  <TooltipRow
                    color="var(--viz-series-2)"
                    label="Opted in to marketing"
                    value={formatNumber(w.cumOptedIn)}
                  />
                  <TooltipRow
                    label="Not opted in"
                    value={formatNumber(w.cumRegistered - w.cumOptedIn)}
                  />
                  <TooltipRow
                    label="Opt-in rate"
                    value={formatOptInRate(w.cumOptedIn, w.cumRegistered)}
                  />
                </>
              )}
            />
          }
        />
        <Legend
          verticalAlign="top"
          height={28}
          iconType="plainline"
          formatter={legendText}
        />
        <Line
          name="Registered"
          dataKey="cumRegistered"
          type="monotone"
          isAnimationActive={false}
          stroke="var(--viz-series-1)"
          strokeWidth={2}
          strokeLinecap="round"
          dot={false}
          activeDot={{ r: 4, stroke: "var(--viz-surface)", strokeWidth: 2 }}
        />
        <Line
          name="Opted in to marketing"
          dataKey="cumOptedIn"
          type="monotone"
          isAnimationActive={false}
          stroke="var(--viz-series-2)"
          strokeWidth={2}
          strokeLinecap="round"
          dot={false}
          activeDot={{ r: 4, stroke: "var(--viz-surface)", strokeWidth: 2 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

/** Share of each week's new shoppers currently opted in, on a full 0–100% axis. */
export function OptInRateChart({
  weeks,
  height = 180,
}: {
  weeks: CumulativeWeek[];
  height?: number;
}) {
  if (weeks.length === 0) return <ChartEmptyState />;
  const data = weeks.map((w) => ({
    ...w,
    rate: optInRate(w.optedIn, w.registered),
  }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={CHART_MARGIN}>
        <CartesianGrid stroke="var(--viz-grid)" vertical={false} />
        {WeekAxis()}
        <YAxis
          domain={[0, 1]}
          ticks={[0, 0.25, 0.5, 0.75, 1]}
          tickFormatter={(v: number) => formatPercent(v, 0)}
          tick={AXIS_TICK}
          axisLine={false}
          tickLine={false}
          width={52}
        />
        <Tooltip
          cursor={{ stroke: "var(--viz-baseline)", strokeWidth: 1 }}
          // A week with no signups has a null rate; without this the tooltip
          // would vanish there instead of reading "—".
          filterNull={false}
          content={
            <WeekTooltip
              rows={(w) => (
                <>
                  <TooltipRow
                    color="var(--viz-series-3)"
                    label="Opt-in rate"
                    value={formatOptInRate(w.optedIn, w.registered)}
                  />
                  <TooltipRow
                    label="Opted in"
                    value={`${formatNumber(w.optedIn)} of ${formatNumber(w.registered)}`}
                  />
                </>
              )}
            />
          }
        />
        <Line
          name="Opt-in rate"
          dataKey="rate"
          type="monotone"
          isAnimationActive={false}
          stroke="var(--viz-series-3)"
          strokeWidth={2}
          strokeLinecap="round"
          dot={false}
          activeDot={{ r: 4, stroke: "var(--viz-surface)", strokeWidth: 2 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
